import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCategories } from '@/query/useCategories';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Category } from '@/model/entities';

import { Button } from '@/components/ui/button';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import { Edit, Trash2 } from 'lucide-react';


interface Props {
  restaurantId: string;
  onEdit?: (category: Category) => void;
  onDelete?: (category: Category) => void;
}
export const CategoriesTable: React.FC<Props> = ({ restaurantId, onEdit, onDelete }) => {
  const { t } = useTranslation('categories');
  const { data: categories, isLoading, isError } = useCategories(restaurantId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  // categories returned by hook are typed in the model

  if (isLoading) return <div className="py-8 text-right">{t('loading', 'טוען קטגוריות...')}</div>;
  if (isError) return <div className="py-8 text-red-500 text-right">{t('error', 'שגיאה בטעינת קטגוריות')}</div>;
  if (!categories || categories.length === 0) return <div className="py-8 text-right text-muted-foreground">{t('empty', 'אין קטגוריות להצגה')}</div>;

  const handleDeleteClick = (cat: Category) => {
    setCategoryToDelete(cat);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete && onDelete) {
      onDelete(categoryToDelete);
    }
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">{t('name', 'שם קטגוריה')}</TableHead>
            <TableHead className="text-right">{t('description', 'תיאור')}</TableHead>
            <TableHead className="text-right">{t('order', 'סדר')}</TableHead>
            <TableHead className="text-right">{t('actions', 'פעולות')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((cat: Category) => {
            const orderVal = cat.order;
            return (
              <TableRow key={cat.id}>
                <TableCell className="text-right">{cat.name}</TableCell>
                <TableCell className="text-right">{cat.description}</TableCell>
                <TableCell className="text-right">{orderVal ?? '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-row gap-2 justify-start">
                    <Button variant="ghost" size="icon" onClick={() => onEdit?.(cat)} aria-label={t('editAria', { name: cat.name, defaultValue: `ערוך ${cat.name}` })}>
                      <Edit className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(cat)} aria-label={t('deleteAria', { name: cat.name, defaultValue: `מחק ${cat.name}` })}>
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>

              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('deleteTitle', 'מחיקת קטגוריה')}
        description={t('deleteConfirm', { name: categoryToDelete?.name ?? '', defaultValue: `האם אתה בטוח שברצונך למחוק את הקטגוריה "${categoryToDelete?.name}"? פעולה זו אינה הפיכה.` })}
      />
    </>
  );
    };

